/**
 * Global arama sözleşmesi (WP 2.11): katlama (normalizasyon), sorgu ayrıştırma, sonek terimleri, eşleşme sınıfı, vurgu ve
 * snippet. **Tek anlam kaynağı:** worker (belge), API (sorgu, vurgu) ve istemci (gösterim) bu modülü kullanır; SQL tarafında
 * katlama yapılmaz (docs/spec/castinflow/search.md §2).
 *
 * Katlama kod noktası başına tanımlıdır (aynı fonksiyon her yerde aynı sonucu verir):
 *   boşluk sınıfı → ' ' · kontrol/biçim/özel kullanım/atanmamış/yüzey karakteri → '' · diğerleri: NFKC → tr-TR küçük harf →
 *   `ı` → `i` → aksan katlama (NFD + birleşen işaretleri sil) → NFC.
 * Sonra ardışık boşluklar teke iner, baş/son boşluk atılır. Böylece `I`, `İ`, `ı`, `i` aynı `i`; `ç/ş/ğ/ö/ü` → `c/s/g/o/u`.
 * Noktalama korunur: `%`, `_`, `\`, `*`, `?` sıradan (literal) karakterdir; joker/desen anlamı YOKTUR.
 */
import { z } from 'zod';
/** Projeksiyon sürümü: belge üretimi değişince artar → backfill/mutabakat yeniden kurar. */
export const SEARCH_PROJECTION_VERSION = 1;
/** Katlanmış sorgu en çok bu kadar karakter (ham girdi ayrıca `SEARCH_RAW_MAX` ile sınırlı). */
export const SEARCH_QUERY_MAX_CHARS = 100;
export const SEARCH_RAW_MAX = 400;
export const SEARCH_MAX_TOKENS = 5;
/** Bu uzunluktan kısa kelimeler yok sayılır (kod noktası). */
export const SEARCH_MIN_TOKEN_CHARS = 2;
/** Terim (sonek) en çok bu kadar karakter; uzun kelimenin soneki kısaltılır. */
export const SEARCH_TERM_MAX = 24;
/** Kaynak metnin indekslenen kısmı (kod noktası). */
export const SEARCH_SOURCE_MAX = 1000;
/** Tam değer anahtarı yalnız bu uzunluğa kadar. */
export const SEARCH_EXACT_MAX = 128;
export const SEARCH_SECTION_LIMIT_MAX = 25;
export const SEARCH_QUICK_LIMIT = 5;
export const SEARCH_COUNT_CAP = 100;
export const SEARCH_TAG_FILTER_MAX = 5;
export const SEARCH_TAG_PREFIX_MAX = 60;
export const SEARCH_SNIPPET_MAX = 160;
/**
 * Aranan kayıt tipleri ve yanıttaki bölüm sırası: M2 çekirdeği (şirket, kişi, ürün) + M3 satış kayıtları (satış akışı sırasıyla).
 * M3 belgesi: aday ad soyad + şirket adı + e-posta; fırsat adı; belge no (TKL-/SPR-/TSP-/SZL-) + konu/ad; başvurulan müşteri /
 * tedarikçi şirketin adı (`reference` kaynağı — yalnız sorgu anında şirketi de görebilen kullanıcıda eşleşir). Tutar, fiyat, marj
 * ve para alanları belgeye GİRMEZ.
 */
export const SEARCH_RECORD_TYPES = [
    'company',
    'contact',
    'product',
    'lead',
    'opportunity',
    'proposal',
    'order',
    'purchase_order',
    'contract',
    // M4 (migration 0057): aktivite adı + özel alanlar (konum aranmaz, AK-8)
    'activity',
    // M4 (migration 0058): destek talebi konusu + özel alanlar (talep sahibi iletişim bilgileri aranmaz)
    'ticket',
    // M5 (migration 0063): form belgesi başlığı (ilk alanın değeri) ve form içi sıra no
    'form_document',
];
// ---------------------------------------------------------------------------------------------------------------------
// Katlama
// ---------------------------------------------------------------------------------------------------------------------
const SPACE_RE = /^[\s\p{Zs}\p{Zl}\p{Zp}]$/u;
const DROP_RE = /^[\p{Cc}\p{Cf}\p{Co}\p{Cn}\p{Cs}]$/u;
const MARK_RE = /\p{M}/gu;
const DROP_ANY_RE = /[\p{Cc}\p{Cf}\p{Co}\p{Cn}\p{Cs}]/gu;
const SPACE_ANY_RE = /[\s\p{Zs}\p{Zl}\p{Zp}]+/gu;
const CACHE_MAX = 20_000;
const cache = new Map();
/** Tek kod noktasının katlanmış karşılığı ('' = atılır, ' ' = ayırıcı). */
export function foldCodePoint(cp) {
    const hit = cache.get(cp);
    if (hit !== undefined)
        return hit;
    let out;
    if (cp >= 0xd800 && cp <= 0xdfff)
        out = '';
    else {
        const ch = String.fromCodePoint(cp);
        if (SPACE_RE.test(ch))
            out = ' ';
        else if (DROP_RE.test(ch))
            out = '';
        else {
            out = ch
                .normalize('NFKC')
                .toLocaleLowerCase('tr-TR')
                .replace(/ı/g, 'i')
                .normalize('NFD')
                .replace(MARK_RE, '')
                .normalize('NFC')
                .replace(DROP_ANY_RE, '')
                .replace(SPACE_ANY_RE, ' ');
        }
    }
    if (cache.size < CACHE_MAX)
        cache.set(cp, out);
    return out;
}
/**
 * Katlama + kaynak eşlemesi (vurgu aralıkları için). `maxSourceChars` verilirse kaynağın yalnız ilk o kadar kod noktası işlenir
 * (indeksleme sınırı).
 */
export function searchFoldWithMap(s, maxSourceChars) {
    const folded = [];
    const srcStart = [];
    const srcEnd = [];
    let i = 0;
    let n = 0;
    let pendingSpace = false;
    while (i < s.length) {
        if (maxSourceChars !== undefined && n >= maxSourceChars)
            break;
        const cp = s.codePointAt(i) ?? 0;
        const w = cp > 0xffff ? 2 : 1;
        // NFKC tek kod noktasından birden çok karakter (ör. `ﬁ` → `fi`) hatta boşluk üretebilir; boşluklar teke iner
        for (const c of foldCodePoint(cp)) {
            if (c === ' ') {
                if (folded.length > 0)
                    pendingSpace = true;
                continue;
            }
            if (pendingSpace) {
                folded.push(' ');
                srcStart.push(i);
                srcEnd.push(i);
                pendingSpace = false;
            }
            for (let k = 0; k < c.length; k += 1) {
                folded.push(c.charAt(k));
                srcStart.push(i);
                srcEnd.push(i + w);
            }
        }
        i += w;
        n += 1;
    }
    return { folded: folded.join(''), srcStart, srcEnd };
}
/** Katlanmış metin (bkz. modül açıklaması). */
export function searchFold(s) {
    return searchFoldWithMap(s).folded;
}
/** Katlanmış metnin kelimeleri (boşlukla ayrılmış; noktalama kelimenin parçasıdır). */
export function searchWords(folded) {
    return folded === '' ? [] : folded.split(' ');
}
/** Kod noktası sayısı */
export function codePointLength(s) {
    let n = 0;
    for (let i = 0; i < s.length; i += 1) {
        const c = s.charCodeAt(i);
        if (c < 0xdc00 || c > 0xdfff)
            n += 1;
    }
    return n;
}
/**
 * Kod noktası dizisi. Terimler bilerek KOD NOKTASI üzerinden tanımlıdır (grafem kümesi değil): katlama zaten birleşen
 * işaretleri siler; aynı tanım worker, API ve istemcide birebir aynıdır.
 */
function codePoints(s) {
    return Array.from(s);
}
function cpSlice(s, start, end) {
    return codePoints(s).slice(start, end).join('');
}
/**
 * Katlanmış metnin sonek terimleri: her kelimenin 2+ kod noktalı bütün sonekleri, en çok `SEARCH_TERM_MAX` kod noktasına
 * kısaltılmış, tekil. Alt dize araması = bu terimlerde önek aralığı.
 */
export function searchTerms(folded) {
    const out = new Set();
    for (const w of searchWords(folded)) {
        const cps = codePoints(w);
        for (let i = 0; i + SEARCH_MIN_TOKEN_CHARS <= cps.length; i += 1)
            out.add(cps.slice(i, i + SEARCH_TERM_MAX).join(''));
    }
    return [...out];
}
/** Sorgu kelimesinin terim aralığı alt sınırı (uzun kelimede ilk `SEARCH_TERM_MAX` kod noktası). */
export function termLowerBound(token) {
    return codePointLength(token) > SEARCH_TERM_MAX ? cpSlice(token, 0, SEARCH_TERM_MAX) : token;
}
/**
 * Önek aralığının üst sınırı: `lo` ile başlayan her dizgeden büyük en küçük dizge (COLLATE "C" = kod noktası sırası).
 * Son kod noktası bir artırılır (vekil aralık atlanır); U+10FFFF ise düşürülüp öncekine geçilir. Boş dönmez (en az 2 karakter).
 */
export function termUpperBound(lo) {
    const cps = codePoints(lo).map((c) => c.codePointAt(0) ?? 0);
    while (cps.length > 0) {
        const last = cps.pop() ?? 0;
        if (last < 0x10ffff) {
            let next = last + 1;
            if (next >= 0xd800 && next <= 0xdfff)
                next = 0xe000;
            cps.push(next);
            return String.fromCodePoint(...cps);
        }
    }
    return String.fromCodePoint(0x10ffff);
}
/** Tam değer anahtarı (≤ `SEARCH_EXACT_MAX` kod noktası; daha uzun değer tam eşleşme adayı olmaz). */
export function exactKeyOf(valueFold) {
    const n = codePointLength(valueFold);
    return n >= 1 && n <= SEARCH_EXACT_MAX ? valueFold : null;
}
export function parseSearchQuery(raw) {
    if (raw.length > SEARCH_RAW_MAX)
        return { ok: false, error: 'too_long' };
    const trimmed = raw.trim();
    if (trimmed.startsWith('#')) {
        const prefix = searchFold(trimmed.slice(1));
        if (prefix === '')
            return { ok: false, error: 'too_short' };
        if (codePointLength(prefix) > SEARCH_TAG_PREFIX_MAX)
            return { ok: false, error: 'too_long' };
        return { ok: true, query: { kind: 'tag', prefix } };
    }
    const folded = searchFold(trimmed);
    if (folded === '')
        return { ok: false, error: 'empty' };
    if (codePointLength(folded) > SEARCH_QUERY_MAX_CHARS)
        return { ok: false, error: 'too_long' };
    const tokens = [];
    for (const w of searchWords(folded)) {
        if (codePointLength(w) < SEARCH_MIN_TOKEN_CHARS || tokens.includes(w))
            continue;
        tokens.push(w);
    }
    if (tokens.length === 0)
        return { ok: false, error: 'too_short' };
    if (tokens.length > SEARCH_MAX_TOKENS)
        return { ok: false, error: 'too_many_tokens' };
    return { ok: true, query: { kind: 'text', tokens, normalized: tokens.join(' ') } };
}
export function matchTier(valueFold, token) {
    if (valueFold === token)
        return 0;
    if (!valueFold.includes(token))
        return null;
    return valueFold.startsWith(token) || valueFold.includes(` ${token}`) ? 1 : 2;
}
/** Görünen metinde kelimelerin eşleştiği aralıklar (UTF-16, kod noktası sınırında; çakışanlar birleşik, sıralı). */
export function highlightRanges(display, tokens) {
    const map = searchFoldWithMap(display);
    const raw = [];
    for (const t of tokens) {
        if (t === '')
            continue;
        let from = 0;
        for (;;) {
            const at = map.folded.indexOf(t, from);
            if (at < 0)
                break;
            const start = map.srcStart[at];
            const end = map.srcEnd[at + t.length - 1];
            if (start === undefined || end === undefined)
                break;
            // Hemen ardından katlamada atılan kod noktaları (birleşen işaret vb.) aralığa katılır: `é` = `e` + U+0301 bölünmez
            raw.push([start, trailingDropped(display, end)]);
            from = at + t.length;
        }
    }
    raw.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const out = [];
    for (const r of raw) {
        const last = out[out.length - 1];
        if (last !== undefined && r[0] <= last[1])
            last[1] = Math.max(last[1], r[1]);
        else
            out.push([r[0], r[1]]);
    }
    return out;
}
/** `end`'den itibaren katlamada tamamen atılan kod noktalarının sonu. */
function trailingDropped(s, end) {
    let i = end;
    while (i < s.length) {
        const cp = s.codePointAt(i) ?? 0;
        if (foldCodePoint(cp) !== '')
            break;
        i += cp > 0xffff ? 2 : 1;
    }
    return i;
}
/** Kod noktası sınırına hizalar (vekil çiftin ortası değil). */
function alignDown(s, i) {
    if (i <= 0)
        return 0;
    if (i >= s.length)
        return s.length;
    const c = s.charCodeAt(i);
    return c >= 0xdc00 && c <= 0xdfff ? i - 1 : i;
}
/**
 * Snippet: görünen metin `max` UTF-16 birimini aşıyorsa ilk eşleşme etrafında pencere (kelime sınırına yaslanır, uçlarda `…`).
 * HTML üretmez; aralıklar düz metin içindir.
 */
export function makeSnippet(display, tokens, max = SEARCH_SNIPPET_MAX) {
    const ranges = highlightRanges(display, tokens);
    if (display.length <= max)
        return { text: display, ranges };
    const first = ranges[0];
    let start = first === undefined ? 0 : Math.max(0, first[0] - Math.floor(max / 4));
    const ws = display.lastIndexOf(' ', start);
    if (ws > 0 && start - ws < 20)
        start = ws + 1;
    start = alignDown(display, start);
    let end = alignDown(display, Math.min(display.length, start + max));
    const we = display.lastIndexOf(' ', end);
    if (end < display.length && we > start + max / 2)
        end = we;
    const pre = start > 0 ? '…' : '';
    const post = end < display.length ? '…' : '';
    const body = display.slice(start, end);
    const shift = pre.length - start;
    const clipped = [];
    for (const [a, b] of ranges) {
        if (b <= start || a >= end)
            continue;
        clipped.push([Math.max(a, start) + shift, Math.min(b, end) + shift]);
    }
    return { text: `${pre}${body}${post}`, ranges: clipped };
}
// ---------------------------------------------------------------------------------------------------------------------
// API sözleşmesi
// ---------------------------------------------------------------------------------------------------------------------
const uuid = z.guid();
export const searchQueryInputSchema = z
    .object({
    q: z.string().max(SEARCH_RAW_MAX),
    types: z.array(z.enum(SEARCH_RECORD_TYPES)).min(1).max(SEARCH_RECORD_TYPES.length).optional(),
    tagIds: z.array(uuid).max(SEARCH_TAG_FILTER_MAX).optional(),
    limit: z.number().int().min(1).max(SEARCH_SECTION_LIMIT_MAX).optional(),
    cursor: z.string().max(2048).optional(),
})
    .strict();
export const SEARCH_MATCH_SOURCES = [
    'core',
    'contact_info',
    'tag',
    'lookup',
    'category',
    'custom_field',
    /** Başvurulan kaydın adı (M3: müşteri/tedarikçi şirket); başvurulan kayıt görünmüyorsa eşleşmez ve gösterilmez */
    'reference',
];
export const searchSnippetSchema = z.object({
    text: z.string(),
    ranges: z.array(z.tuple([z.number().int().min(0), z.number().int().min(0)])),
});
export const searchHitSchema = z.object({
    id: uuid,
    type: z.enum(SEARCH_RECORD_TYPES),
    title: z.string(),
    titleRanges: z.array(z.tuple([z.number().int().min(0), z.number().int().min(0)])),
    subtitle: z.string().nullable(),
    href: z.string(),
    match: z
        .object({
        kind: z.enum(SEARCH_MATCH_SOURCES),
        label: z.string(),
        snippet: searchSnippetSchema,
    })
        .nullable(),
});
export const searchSectionSchema = z.object({
    type: z.enum(SEARCH_RECORD_TYPES),
    items: z.array(searchHitSchema),
    /**
     * `exact: true` → kesin sayı (yalnız bütün adaylar değerlendirildiğinde). `exact: false` → alt sınır; `partial` iken yalnız bu
     * yanıtta bulunan GÖRÜNÜR sonuçları ifade eder ("gösterilen"), toplam değildir. Görünmeyen adaylar hiçbir sayıya girmez.
     */
    count: z.object({ value: z.number().int().min(0), exact: z.boolean() }),
    /** Devam imleci; yalnız `hasMore` iken dolu */
    nextCursor: z.string().nullable(),
    /**
     * K-2.11-A: aday taraması tamamlanmadı — bu yanıt sınırlı bir aday penceresidir; boş olsa bile "sonuç yok" DEĞİLDİR.
     * Yalnız `scan` kipinde `true` olabilir.
     */
    partial: z.boolean(),
    /** Devam imleciyle alınabilecek sonuç veya incelenmemiş aday var */
    hasMore: z.boolean(),
    /**
     * `relevance` = bütün adaylar değerlendirildi (eşleşme sınıfı + ağırlık + güncellik); `scan` = geniş sorgu (tam değer
     * eşleşmeleri önce, sonra arama indeksi sırasıyla sınırlı aday pencereleri).
     */
    order: z.enum(['relevance', 'scan']),
});
export const searchResponseSchema = z.object({
    query: z.union([
        z.object({ kind: z.literal('text'), tokens: z.array(z.string()) }),
        z.object({
            kind: z.literal('tag'),
            prefix: z.string(),
            tags: z.array(z.object({ id: uuid, name: z.string() })),
        }),
    ]),
    sections: z.array(searchSectionSchema),
    index: z.object({
        state: z.enum(['ready', 'building']),
        /** Aranabilir hâle gelmemiş değişiklik var (eventual consistency; K-9) */
        pending: z.boolean(),
    }),
});
//# sourceMappingURL=search.js.map